import { useCallback } from "react";
import { NavigateFunction } from "react-router-dom";
import {
  showSuccess,
  showToastError,
  showToastSuccess,
} from "../shared/alertService";
import type { UsePostsModalsReturn } from "./usePostsModals";

interface UsePostsActionsProps {
  approvePost: (postId: number) => Promise<void>;
  rejectPost: (postId: number, reason: string) => Promise<void>;
  revertToPending: (postId: number) => Promise<void>;
  editPost: (
    postId: number,
    data: {
      content: string;
      hashtags: string[];
      call_to_action?: string;
      call_to_action_url?: string;
      media_gallery?: any[];
    },
  ) => Promise<void>;
  schedulePost: (postId: number, date: string, time: string) => Promise<void>;
  reschedulePostPublished: (
    postId: number,
    date: string,
    time: string,
  ) => Promise<void>;
  unschedulePost: (postId: number) => Promise<void>;
  deletePost: (postId: number) => Promise<void>;
  uploadImage: (postId: number, file: File) => Promise<any>;
  uploadVideo: (
    postId: number,
    file: File,
    destination: string,
  ) => Promise<any>;
  modals: UsePostsModalsReturn;
  navigate: NavigateFunction;
}

export interface UsePostsActionsReturn {
  handleApprove: (postId: number) => Promise<void>;
  handleReject: (
    postId: number,
    reason: string,
    successMessage?: string,
  ) => Promise<void>;
  handleRevertToPending: (postId: number) => Promise<void>;
  handleDelete: (postId: number) => Promise<void>;
  handleUnschedule: (postId: number) => Promise<void>;
  handleEdit: () => Promise<void>;
  handleSchedule: () => Promise<void>;
  handleReschedule: (
    postId: number,
    date: string,
    time: string,
  ) => Promise<void>;
}

export function usePostsActions({
  approvePost,
  rejectPost,
  revertToPending,
  editPost,
  schedulePost,
  reschedulePostPublished,
  unschedulePost,
  deletePost,
  uploadImage,
  uploadVideo,
  modals,
  navigate,
}: UsePostsActionsProps): UsePostsActionsReturn {
  const handleApprove = useCallback(
    async (postId: number) => {
      try {
        await approvePost(postId);
        showToastSuccess("Post aprovado com sucesso!");
      } catch (err: any) {
        showToastError(err.message || "Erro ao aprovar post.");
      }
    },
    [approvePost],
  );

  const handleReject = useCallback(
    async (postId: number, reason: string, successMessage?: string) => {
      try {
        await rejectPost(postId, reason);
        showToastSuccess(successMessage || "Post rejeitado!");
        modals.setRejectModalOpen(false);
        modals.setRejectPostId(null);
        modals.setRejectReason("");
      } catch (err: any) {
        showToastError(err.message || "Erro ao rejeitar post.");
      }
    },
    [rejectPost, modals],
  );

  const handleRevertToPending = useCallback(
    async (postId: number) => {
      try {
        await revertToPending(postId);
        showToastSuccess("Post revertido para pendente com sucesso!");
      } catch (err: any) {
        showToastError(err.message || "Erro ao reverter post para pendente.");
      }
    },
    [revertToPending],
  );

  const handleDelete = useCallback(
    async (postId: number) => {
      try {
        await deletePost(postId);
        showToastSuccess("Post excluído com sucesso!");
        modals.setShowDeleteModal(null);
      } catch (err: any) {
        showToastError(err.message || "Erro ao excluir post.");
      }
    },
    [deletePost, modals],
  );

  const handleUnschedule = useCallback(
    async (postId: number) => {
      try {
        await unschedulePost(postId);
        showToastSuccess("Agendamento removido com sucesso!");
      } catch (err: any) {
        showToastError(err.message || "Erro ao remover agendamento.");
      }
    },
    [unschedulePost],
  );

  const handleEdit = useCallback(async () => {
    if (modals.editingPost !== null) {
      try {
        // 1. Processar hashtags
        let hashtagsArray: string[] = [];
        if (modals.editHashtags) {
          hashtagsArray = modals.editHashtags
            .split(/\s+/)
            .map((tag: string) =>
              tag.trim().replace(/^#+/, "").replace(/['",]/g, ""),
            )
            .filter((tag: string) => tag.length > 0);
        }

        // 2. Processar uploads de mídia novos
        const updatedGallery = [];
        for (const item of modals.editGalleryItems) {
          if (item.file) {
            // Upload novo
            try {
              let res;
              if (item.type === "image") {
                res = await uploadImage(modals.editingPost, item.file);
              } else {
                res = await uploadVideo(modals.editingPost, item.file, "feed");
              }

              const info =
                res.image_info ||
                res.video_info ||
                res.data?.image_info ||
                res.data?.video_info;
              updatedGallery.push({
                url: info?.public_url || info?.url || res.url,
                type: item.type,
                filename: info?.filename,
              });
            } catch (err: any) {
              console.error("Erro no upload de item da galeria:", err);
              // Repassa o motivo do backend (ex.: "Arquivo muito grande...") em vez de genérico.
              const reason = err?.errorData?.error || err?.message;
              throw new Error(
                reason
                  ? `Falha ao enviar "${item.file.name}": ${reason}`
                  : "Falha ao subir um ou mais arquivos da galeria.",
              );
            }
          } else {
            // Item já existente (apenas manter a URL)
            updatedGallery.push({
              url: item.preview,
              type: item.type,
              id: item.id,
            });
          }
        }

        // 3. Atualizar o post
        await editPost(modals.editingPost, {
          content: modals.editContent,
          hashtags: hashtagsArray,
          call_to_action: modals.editCallToAction || undefined,
          call_to_action_url: modals.editCallToActionUrl?.trim() || undefined,
          media_gallery: updatedGallery.length > 0 ? updatedGallery : undefined,
        } as any);

        showToastSuccess("Post atualizado com sucesso!");
        modals.setEditingPost(null);
        modals.setEditGalleryItems([]);
      } catch (err: any) {
        showToastError(err.message || "Erro ao editar post.");
      }
    }
  }, [editPost, uploadImage, uploadVideo, modals]);

  const handleSchedule = useCallback(async () => {
    if (
      modals.showScheduleModal !== null &&
      modals.scheduleDate &&
      modals.scheduleTime
    ) {
      try {
        modals.setSchedulingPost(modals.showScheduleModal);
        await schedulePost(
          modals.showScheduleModal,
          modals.scheduleDate,
          modals.scheduleTime,
        );

        modals.setShowScheduleModal(null);
        modals.setScheduleDate("");
        modals.setScheduleTime("09:00");

        // Melhoria UX: Oferecer para ver o cronograma
        showSuccess(
          "Seu post foi agendado com sucesso e agora faz parte do seu cronograma editorial.",
          "Post Agendado",
          {
            confirmButtonText: "Ver no Cronograma",
            showCancelButton: true,
            cancelButtonText: "Continuar aqui",
            confirmButtonColor: "#10b981",
          },
        ).then((result) => {
          if (result.isConfirmed) {
            navigate("/posts/calendar");
          }
        });
      } catch (err: any) {
        showToastError(err.message || "Erro ao agendar post.");
      } finally {
        modals.setSchedulingPost(null);
      }
    }
  }, [schedulePost, modals, navigate]);

  return {
    handleApprove,
    handleReject,
    handleRevertToPending,
    handleDelete,
    handleUnschedule,
    handleEdit,
    handleSchedule,
    handleReschedule: useCallback(
      async (postId: number, date: string, time: string) => {
        try {
          await reschedulePostPublished(postId, date, time);
          showToastSuccess("Post duplicado e reagendado com sucesso!");
          modals.setShowScheduleModal(null);
        } catch (err: any) {
          showToastError(err.message || "Erro ao reagendar post.");
        }
      },
      [reschedulePostPublished, modals],
    ),
  };
}
